/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { ILeagueModel } from "@bms/common";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ITableRow, LeaguePlayerRow } from "./LeaguePlayerRow";
import { LeagueTableHeader } from "./LeagueTableHeader";
import { IAggregatedTeam, LeagueTeamRow } from "./LeagueTeamRow";

import "./LeagueTableTab.scss";

interface ILeagueTableTabProps {
  league: ILeagueModel;
  highlightTeamNames?: string[];
  withGoals?: boolean;
}

export const LeagueTableTab = ({
  league,
  highlightTeamNames,
  withGoals = true,
}: ILeagueTableTabProps) => {
  const { t } = useTranslation();

  const rows = useMemo<ITableRow[]>(() => {
    const sorted = [...(league.Table ?? [])].sort(
      (a, b) => (b.Points ?? 0) - (a.Points ?? 0),
    );
    return sorted.map((row, index) => ({ ...row, Position: index + 1 }));
  }, [league.Table]);

  const hasPlayers = rows.some((r) => r.PlayerId != null);

  const hasMatchStats = rows.some(
    (r) =>
      r.MatchesPlayed != null ||
      r.Wins != null ||
      r.Draws != null ||
      r.Losses != null,
  );

  const hasGoals =
    withGoals &&
    rows.some((r) => r.GoalsScored != null || r.GoalsConceded != null);

  const aggregatedTeams = useMemo<IAggregatedTeam[]>(() => {
    const teamMap = new Map<string, IAggregatedTeam>();
    for (const row of rows) {
      if (!row.TeamId) continue;
      const key = String(row.TeamId);
      const existing = teamMap.get(key);
      if (!existing) {
        teamMap.set(key, {
          TeamId: row.TeamId,
          TeamName: row.TeamName,
          TeamMediaId: row.TeamMediaId,
          TeamMediaTypeCode: row.TeamMediaTypeCode,
          MatchesPlayed: row.MatchesPlayed ?? 0,
          Wins: row.Wins ?? 0,
          Draws: row.Draws ?? 0,
          Losses: row.Losses ?? 0,
          GoalsScored: row.GoalsScored ?? 0,
          GoalsConceded: row.GoalsConceded ?? 0,
          Points: row.Points ?? 0,
          Position: 0,
        });
      } else {
        existing.MatchesPlayed += row.MatchesPlayed ?? 0;
        existing.Wins += row.Wins ?? 0;
        existing.Draws += row.Draws ?? 0;
        existing.Losses += row.Losses ?? 0;
        existing.GoalsScored += row.GoalsScored ?? 0;
        existing.GoalsConceded += row.GoalsConceded ?? 0;
        existing.Points += row.Points ?? 0;
      }
    }
    return Array.from(teamMap.values())
      .sort((a, b) => b.Points - a.Points)
      .map((team, index) => ({ ...team, Position: index + 1 }));
  }, [rows]);

  const renderStatsHeaders = () => (
    <>
      {hasMatchStats && (
        <>
          <th className="LeagueTableTab__col--stat">
            {t("LEAGUE_TABLE_COLUMN_PLAYED_SHORT")}
          </th>
          <th className="LeagueTableTab__col--stat">
            {t("LEAGUE_TABLE_COLUMN_WINS_SHORT")}
          </th>
          <th className="LeagueTableTab__col--stat">
            {t("LEAGUE_TABLE_COLUMN_DRAWS_SHORT")}
          </th>
          <th className="LeagueTableTab__col--stat">
            {t("LEAGUE_TABLE_COLUMN_LOSSES_SHORT")}
          </th>
        </>
      )}
      {hasGoals && (
        <th className="LeagueTableTab__col--goals">
          {t("LEAGUE_TABLE_COLUMN_GOALS_SHORT")}
        </th>
      )}
      <th className="LeagueTableTab__col--pts">
        {t("LEAGUE_TABLE_COLUMN_POINTS_SHORT")}
      </th>
    </>
  );

  return (
    <div className="LeagueTableTab">
      <LeagueTableHeader league={league} />

      {hasPlayers && (
        <div className="LeagueTableTab__section-title">
          {t("LEAGUE_TABLE_SECTION_TEAMS")}
        </div>
      )}
      <div className="LeagueTableTab__table-wrapper">
        <table className="LeagueTableTab__table">
          <thead>
            <tr>
              <th className="LeagueTableTab__col--pos">#</th>
              <th className="LeagueTableTab__col--team">
                {t("LEAGUE_TABLE_COLUMN_TEAM")}
              </th>
              {renderStatsHeaders()}
            </tr>
          </thead>
          <tbody>
            {aggregatedTeams.map((team) => (
              <LeagueTeamRow
                key={String(team.TeamId)}
                team={team}
                highlightTeamNames={highlightTeamNames}
                hasMatchStats={hasMatchStats}
                hasGoals={hasGoals}
              />
            ))}
          </tbody>
        </table>
      </div>

      {hasPlayers && (
        <>
          <div className="LeagueTableTab__section-title">
            {t("LEAGUE_TABLE_SECTION_PLAYERS")}
          </div>
          <div className="LeagueTableTab__table-wrapper">
            <table className="LeagueTableTab__table">
              <thead>
                <tr>
                  <th className="LeagueTableTab__col--pos">#</th>
                  <th className="LeagueTableTab__col--team">
                    {t("LEAGUE_TABLE_COLUMN_PLAYER")}
                  </th>
                  <th className="LeagueTableTab__col--team">
                    {t("LEAGUE_TABLE_COLUMN_TEAM")}
                  </th>
                  {renderStatsHeaders()}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <LeaguePlayerRow
                    key={row.Id}
                    row={row}
                    hasMatchStats={hasMatchStats}
                    hasGoals={hasGoals}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
